/**
 * Studio · Developers → Política y límites (REQ-GW-014, ADR-135).
 *
 * Shows the effective (inherited) configuration for a reference key and lets
 * admins harden the policy at tenant/application/key scope. The backend is the
 * source of truth for the hardening rules (422 POLICY_RELAXATION_FORBIDDEN);
 * the UI only guides/clamps. No internal ids are rendered (ISS-634; guide §8.3).
 */

import { useEffect, useMemo, useState } from 'react';
import { Lock, ShieldCheck, SlidersHorizontal } from 'lucide-react';
import { PageHeader } from '@vanguard-atlas/shared';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Spinner,
} from '@/components/ui';
import { useI18n } from '@/i18n';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import { useGatewayApplications, useGatewayKeys } from '../hooks/useGateway';
import { useDeletePolicy, useEffectivePolicy, usePolicies, useUpsertPolicy } from '../hooks/usePolicy';
import type { GatewayPolicySections, PolicyScope } from '../types';

const SCOPES: PolicyScope[] = ['tenant', 'application', 'key', 'end_user'];
const TRACE_ORDER = [
  'request',
  'end_user',
  'key',
  'application',
  'tenant',
  'legacy_rate_limit',
  'legacy_budget',
  'platform',
];

interface FormState {
  rpm: string;
  tpm: string;
  concurrency: string;
  maxTokens: string;
  defaultMaxTokens: string;
  budget: string;
  ttl: string;
  guardrails: string;
  defaultModel: string;
  residency: string;
}

const EMPTY_FORM: FormState = {
  rpm: '',
  tpm: '',
  concurrency: '',
  maxTokens: '',
  defaultMaxTokens: '',
  budget: '',
  ttl: '',
  guardrails: '',
  defaultModel: '',
  residency: '',
};

export function GatewayPolicyPage() {
  const { t } = useI18n();
  const canEdit = useModuleAccess('ai_gateway', 'admin');
  const applications = useGatewayApplications();
  const [applicationId, setApplicationId] = useState('');
  const [keyId, setKeyId] = useState('');
  const [scope, setScope] = useState<PolicyScope>('tenant');
  const [endUserId, setEndUserId] = useState('');
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [errorFields, setErrorFields] = useState<string[]>([]);
  const [notice, setNotice] = useState<string | null>(null);

  const apps = applications.data?.data ?? [];
  const keysQuery = useGatewayKeys(applicationId || null);
  const keys = keysQuery.data?.data ?? [];
  const selectedApp = apps.find((app) => app.id === applicationId);
  const tenantId = selectedApp?.tenant_id ?? '';

  const scopeId = useMemo(() => {
    if (scope === 'tenant') return tenantId;
    if (scope === 'application') return applicationId;
    if (scope === 'key') return keyId;
    return ''; // end_user: the API hashes the identifier server-side
  }, [scope, tenantId, applicationId, keyId]);

  const endUser = scope === 'end_user' ? endUserId.trim() : '';
  const effective = useEffectivePolicy(keyId || null, endUser || undefined);
  const policies = usePolicies(scope, scopeId, endUser || undefined);
  const upsert = useUpsertPolicy();
  const remove = useDeletePolicy();
  const currentPolicy = policies.data && policies.data.length > 0 ? policies.data[0] : null;

  useEffect(() => {
    if (!applicationId && apps.length > 0) setApplicationId(apps[0].id);
  }, [apps, applicationId]);

  useEffect(() => {
    if (keys.length > 0 && !keys.some((key) => key.id === keyId)) setKeyId(keys[0].id);
  }, [keys, keyId]);

  useEffect(() => {
    const policy = currentPolicy?.policy ?? {};
    setForm({
      rpm: policy.rate_limits?.rpm?.toString() ?? '',
      tpm: policy.rate_limits?.tpm?.toString() ?? '',
      concurrency: policy.rate_limits?.concurrency?.toString() ?? '',
      maxTokens: policy.limits?.max_tokens?.toString() ?? '',
      defaultMaxTokens: policy.limits?.default_max_tokens?.toString() ?? '',
      budget: policy.budgets?.amount_usd?.toString() ?? '',
      ttl: policy.cache?.ttl_seconds?.toString() ?? '',
      guardrails: policy.data?.guardrails_mode ?? '',
      defaultModel: policy.configuration?.default_model ?? '',
      residency: policy.configuration?.residency ?? '',
    });
    setErrorKey(null);
    setErrorFields([]);
    setNotice(null);
  }, [currentPolicy]);

  const effectivePolicy = effective.data?.policy ?? {};
  const trace = effective.data?.source_trace ?? {};

  const originFor = (section: string, field: string): string => {
    for (const scopeName of TRACE_ORDER) {
      const entry = trace[scopeName];
      if (entry && typeof entry === 'object' && `${section}.${field}` in entry) {
        return scopeName;
      }
    }
    return 'platform';
  };

  const originLabel = (origin: string): string => {
    if (origin === 'platform') return t('gateway.policy.effective.platform');
    if (origin === 'tenant') return t('gateway.policy.effective.tenant');
    if (origin === 'application') return t('gateway.policy.effective.application');
    if (origin === 'key') return t('gateway.policy.effective.key');
    if (origin === 'end_user') return t('gateway.policy.effective.endUser');
    return t('gateway.policy.effective.inherited');
  };

  const effectiveRows = [
    { id: 'rpm', section: 'rate_limits', field: 'rpm', label: t('gateway.policy.fields.rpm'), value: effectivePolicy.rate_limits?.rpm },
    { id: 'tpm', section: 'rate_limits', field: 'tpm', label: t('gateway.policy.fields.tpm'), value: effectivePolicy.rate_limits?.tpm },
    { id: 'concurrency', section: 'rate_limits', field: 'concurrency', label: t('gateway.policy.fields.concurrency'), value: effectivePolicy.rate_limits?.concurrency },
    { id: 'window', section: 'rate_limits', field: 'window_seconds', label: t('gateway.policy.fields.window'), value: effectivePolicy.rate_limits?.window_seconds },
    { id: 'maxTokens', section: 'limits', field: 'max_tokens', label: t('gateway.policy.fields.maxTokens'), value: effectivePolicy.limits?.max_tokens },
    { id: 'defaultMaxTokens', section: 'limits', field: 'default_max_tokens', label: t('gateway.policy.fields.defaultMaxTokens'), value: effectivePolicy.limits?.default_max_tokens },
    { id: 'budget', section: 'budgets', field: 'amount_usd', label: t('gateway.policy.fields.budget'), value: effectivePolicy.budgets?.amount_usd },
    { id: 'ttl', section: 'cache', field: 'ttl_seconds', label: t('gateway.policy.fields.ttl'), value: effectivePolicy.cache?.ttl_seconds },
    { id: 'guardrails', section: 'data', field: 'guardrails_mode', label: t('gateway.policy.fields.guardrails'), value: effectivePolicy.data?.guardrails_mode },
  ];

  const parseNumber = (value: string): number | undefined => {
    if (value.trim() === '') return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const buildPolicy = (): GatewayPolicySections => {
    const policy: GatewayPolicySections = {};
    const rpm = parseNumber(form.rpm);
    const tpm = parseNumber(form.tpm);
    const concurrency = parseNumber(form.concurrency);
    if (rpm !== undefined || tpm !== undefined || concurrency !== undefined) {
      policy.rate_limits = {};
      if (rpm !== undefined) policy.rate_limits.rpm = rpm;
      if (tpm !== undefined) policy.rate_limits.tpm = tpm;
      if (concurrency !== undefined) policy.rate_limits.concurrency = concurrency;
    }
    const maxTokens = parseNumber(form.maxTokens);
    const defaultMaxTokens = parseNumber(form.defaultMaxTokens);
    if (maxTokens !== undefined || defaultMaxTokens !== undefined) {
      policy.limits = {};
      if (maxTokens !== undefined) policy.limits.max_tokens = maxTokens;
      if (defaultMaxTokens !== undefined) {
        policy.limits.default_max_tokens = defaultMaxTokens;
      }
    }
    const budget = parseNumber(form.budget);
    if (budget !== undefined) policy.budgets = { amount_usd: budget };
    const ttl = parseNumber(form.ttl);
    if (ttl !== undefined) policy.cache = { ttl_seconds: ttl };
    if (form.guardrails) {
      policy.data = { guardrails_mode: form.guardrails as 'off' | 'monitor' | 'block' };
    }
    if (form.defaultModel.trim() || form.residency.trim()) {
      policy.configuration = {
        default_model: form.defaultModel.trim() || null,
        residency: form.residency.trim() || null,
      };
    }
    return policy;
  };

  const save = async (): Promise<void> => {
    setErrorKey(null);
    setErrorFields([]);
    setNotice(null);
    try {
      await upsert.mutateAsync({
        scope,
        scope_id: scope === 'end_user' ? undefined : scopeId,
        end_user: scope === 'end_user' ? endUserId.trim() : undefined,
        policy: buildPolicy(),
      });
      setNotice(t('gateway.policy.edit.saved'));
      await effective.refetch();
      await policies.refetch();
    } catch (err: unknown) {
      const response = err as { response?: { data?: { detail?: { code?: string; fields?: string[] } } } };
      const detail = response.response?.data?.detail;
      if (detail?.code === 'POLICY_RELAXATION_FORBIDDEN' && detail.fields) {
        setErrorKey('gateway.policy.edit.relaxation');
        setErrorFields(detail.fields);
      } else {
        setErrorKey('gateway.policy.edit.error');
      }
    }
  };

  const reset = async (): Promise<void> => {
    if (!currentPolicy) return;
    await remove.mutateAsync(currentPolicy.id);
    setNotice(t('gateway.policy.edit.deleted'));
    await effective.refetch();
    await policies.refetch();
  };

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        icon={ShieldCheck}
        title={t('gateway.policy.title')}
        subtitle={t('gateway.policy.subtitle')}
        testId="gateway-policy-header"
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Lock className="h-5 w-5 text-primary" aria-hidden="true" />
            {t('gateway.policy.reference')}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <div>
            <Label htmlFor="policy-application">{t('gateway.policy.application')}</Label>
            <select
              id="policy-application"
              data-testid="policy-application"
              className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
              value={applicationId}
              onChange={(event) => setApplicationId(event.target.value)}
            >
              {apps.map((app) => (
                <option key={app.id} value={app.id}>
                  {app.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="policy-key">{t('gateway.policy.key')}</Label>
            <select
              id="policy-key"
              data-testid="policy-key"
              className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
              value={keyId}
              onChange={(event) => setKeyId(event.target.value)}
            >
              {keys.map((key) => (
                <option key={key.id} value={key.id}>
                  {key.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="policy-scope">{t('gateway.policy.scopeLabel')}</Label>
            <select
              id="policy-scope"
              data-testid="policy-scope"
              className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
              value={scope}
              onChange={(event) => setScope(event.target.value as PolicyScope)}
            >
              {SCOPES.map((value) => (
                <option key={value} value={value}>
                  {t(`gateway.policy.scopes.${value}`)}
                </option>
              ))}
            </select>
          </div>
          {scope === 'end_user' ? (
            <div>
              <Label htmlFor="policy-end-user">{t('gateway.policy.endUserLabel')}</Label>
              <Input
                id="policy-end-user"
                data-testid="policy-end-user"
                className="mt-1"
                placeholder={t('gateway.policy.endUserPlaceholder')}
                value={endUserId}
                onChange={(event) => setEndUserId(event.target.value)}
              />
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <SlidersHorizontal className="h-5 w-5 text-blue-600" aria-hidden="true" />
            {t('gateway.policy.config.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {t('gateway.policy.config.description')}
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label
                className="mb-1 block text-sm text-gray-600 dark:text-gray-300"
                htmlFor="policy-default-model"
              >
                {t('gateway.policy.config.defaultModel')}
              </label>
              <input
                id="policy-default-model"
                data-testid="policy-default-model"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:opacity-50"
                value={form.defaultModel}
                onChange={(event) =>
                  setForm((current) => ({ ...current, defaultModel: event.target.value }))
                }
                placeholder="claude-haiku-4-5"
                disabled={!canEdit}
              />
              <p className="mt-1 text-xs text-gray-500">
                {t('gateway.policy.config.defaultModelHint')}
              </p>
            </div>
            <div>
              <label
                className="mb-1 block text-sm text-gray-600 dark:text-gray-300"
                htmlFor="policy-residency"
              >
                {t('gateway.policy.config.residency')}
              </label>
              <input
                id="policy-residency"
                data-testid="policy-residency"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:opacity-50"
                value={form.residency}
                onChange={(event) =>
                  setForm((current) => ({ ...current, residency: event.target.value }))
                }
                placeholder={t('gateway.policy.config.residencyPlaceholder')}
                disabled={!canEdit}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-5 w-5 text-emerald-600" aria-hidden="true" />
            {t('gateway.policy.effective.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!keyId ? (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {t('gateway.policy.empty')}
            </p>
          ) : effective.isLoading ? (
            <Spinner />
          ) : effective.isError ? (
            <p className="text-sm text-red-600">{t('gateway.policy.loadError')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500 dark:border-gray-700">
                    <th className="py-2 pr-4">{t('gateway.policy.effective.field')}</th>
                    <th className="py-2 pr-4">{t('gateway.policy.effective.value')}</th>
                    <th className="py-2">{t('gateway.policy.effective.origin')}</th>
                  </tr>
                </thead>
                <tbody>
                  {effectiveRows.map((row) => (
                    <tr
                      key={row.id}
                      data-testid={`policy-effective-${row.id}`}
                      className="border-b last:border-0 dark:border-gray-800"
                    >
                      <td className="py-2 pr-4">{row.label}</td>
                      <td className="py-2 pr-4 font-mono">{row.value ?? '—'}</td>
                      <td className="py-2 text-gray-500">
                        {originLabel(originFor(row.section, row.field))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {canEdit ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldCheck className="h-5 w-5 text-primary" aria-hidden="true" />
              {t('gateway.policy.edit.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {t('gateway.policy.edit.hint')}
            </p>
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <Label htmlFor="policy-rpm">{t('gateway.policy.fields.rpm')}</Label>
                <Input
                  id="policy-rpm"
                  data-testid="policy-input-rpm"
                  type="number"
                  min={1}
                  value={form.rpm}
                  placeholder={String(effectivePolicy.rate_limits?.rpm ?? '')}
                  onChange={(event) => setForm({ ...form, rpm: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="policy-tpm">{t('gateway.policy.fields.tpm')}</Label>
                <Input
                  id="policy-tpm"
                  type="number"
                  min={1}
                  value={form.tpm}
                  placeholder={String(effectivePolicy.rate_limits?.tpm ?? '')}
                  onChange={(event) => setForm({ ...form, tpm: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="policy-concurrency">{t('gateway.policy.fields.concurrency')}</Label>
                <Input
                  id="policy-concurrency"
                  type="number"
                  min={1}
                  value={form.concurrency}
                  placeholder={String(effectivePolicy.rate_limits?.concurrency ?? '')}
                  onChange={(event) => setForm({ ...form, concurrency: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="policy-max-tokens">{t('gateway.policy.fields.maxTokens')}</Label>
                <Input
                  id="policy-max-tokens"
                  type="number"
                  min={1}
                  value={form.maxTokens}
                  placeholder={String(effectivePolicy.limits?.max_tokens ?? '')}
                  onChange={(event) => setForm({ ...form, maxTokens: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="policy-default-max-tokens">
                  {t('gateway.policy.fields.defaultMaxTokens')}
                </Label>
                <Input
                  id="policy-default-max-tokens"
                  type="number"
                  min={1}
                  value={form.defaultMaxTokens}
                  placeholder={String(effectivePolicy.limits?.default_max_tokens ?? '')}
                  onChange={(event) =>
                    setForm({ ...form, defaultMaxTokens: event.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="policy-budget">{t('gateway.policy.fields.budget')}</Label>
                <Input
                  id="policy-budget"
                  type="number"
                  min={0}
                  value={form.budget}
                  placeholder={String(effectivePolicy.budgets?.amount_usd ?? '')}
                  onChange={(event) => setForm({ ...form, budget: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="policy-ttl">{t('gateway.policy.fields.ttl')}</Label>
                <Input
                  id="policy-ttl"
                  type="number"
                  min={1}
                  value={form.ttl}
                  placeholder={String(effectivePolicy.cache?.ttl_seconds ?? '')}
                  onChange={(event) => setForm({ ...form, ttl: event.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="policy-guardrails">{t('gateway.policy.fields.guardrails')}</Label>
                <select
                  id="policy-guardrails"
                  className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
                  value={form.guardrails}
                  onChange={(event) => setForm({ ...form, guardrails: event.target.value })}
                >
                  <option value="">{t('gateway.policy.guardrails.inherit')}</option>
                  <option value="off">{t('gateway.policy.guardrails.off')}</option>
                  <option value="monitor">{t('gateway.policy.guardrails.monitor')}</option>
                  <option value="block">{t('gateway.policy.guardrails.block')}</option>
                </select>
              </div>
            </div>

            {errorKey ? (
              <p data-testid="policy-error" className="text-sm text-red-600">
                {t(errorKey, { fields: errorFields.join(', ') })}
              </p>
            ) : null}
            {notice ? (
              <p data-testid="policy-notice" className="text-sm text-emerald-600">
                {notice}
              </p>
            ) : null}

            <div className="flex flex-wrap gap-2">
              <Button
                data-testid="policy-save"
                onClick={() => void save()}
                disabled={upsert.isPending || (scope === 'end_user' ? !endUserId.trim() : !scopeId)}
              >
                <Lock className="mr-1 h-4 w-4" aria-hidden="true" />
                {upsert.isPending ? t('gateway.policy.edit.saving') : t('gateway.policy.edit.save')}
              </Button>
              {currentPolicy ? (
                <Button
                  variant="outline"
                  data-testid="policy-delete"
                  onClick={() => void reset()}
                  disabled={remove.isPending}
                >
                  {t('gateway.policy.edit.delete')}
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-4 text-sm text-gray-600 dark:text-gray-300">
            {t('gateway.policy.edit.readOnly')}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
